import { Component } from '@theme/component';

/**
 * Hero slideshow with promotion countdown timer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slidesContainer - The slides wrapper.
 * @property {HTMLElement[]} slides - Individual slide elements.
 * @property {HTMLElement[]} [imageSlides] - Image slide elements.
 * @property {HTMLElement} prevBtn - Previous slide button.
 * @property {HTMLElement} nextBtn - Next slide button.
 * @property {HTMLElement} [dotsContainer] - Container for nav dots between arrows.
 * @property {HTMLElement} [countdownHours] - Countdown hours display.
 * @property {HTMLElement} [countdownMinutes] - Countdown minutes display.
 * @property {HTMLElement} [countdownSeconds] - Countdown seconds display.
 *
 * @extends Component<Refs>
 */
class PvHeroPromo extends Component {
  requiredRefs = ['slidesContainer', 'slides'];

  #currentSlide = 0;
  #autoplayInterval = null;
  #countdownInterval = null;
  #cancelAutoplayTriggers = null;

  connectedCallback() {
    super.connectedCallback();

    const { slides, prevBtn, nextBtn, dotsContainer } = this.refs;
    if (!slides || slides.length === 0) return;

    if (prevBtn) prevBtn.addEventListener('click', () => this.#prevSlide());
    if (nextBtn) nextBtn.addEventListener('click', () => this.#nextSlide());

    // Dot navigation
    if (dotsContainer) {
      const dots = dotsContainer.querySelectorAll('.pv-hero-promo__nav-dot');
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index || '0', 10);
          this.#goToSlide(index);
          this.#resetAutoplay();
        });
      });
    }

    // Touch/swipe support
    let touchStartX = 0;
    this.refs.slidesContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    this.refs.slidesContainer.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.#nextSlide() : this.#prevSlide();
      }
    }, { passive: true });

    // Autoplay — deferred until the first user interaction. Auto-advancing
    // before the browser finalizes LCP (which happens on first input) makes the
    // slide swap register as a late LCP candidate, inflating the metric.
    const autoplaySpeed = parseInt(this.getAttribute('data-autoplay-speed') || '5000', 10);
    if (autoplaySpeed > 0) {
      const triggers = ['pointerdown', 'keydown', 'touchstart'];
      const startAutoplay = () => {
        this.#cancelAutoplayTriggers?.();
        this.#autoplayInterval = setInterval(() => this.#nextSlide(), autoplaySpeed);
      };
      this.#cancelAutoplayTriggers = () => {
        triggers.forEach((evt) => window.removeEventListener(evt, startAutoplay));
        this.#cancelAutoplayTriggers = null;
      };
      triggers.forEach((evt) => window.addEventListener(evt, startAutoplay, { once: true, passive: true }));
    }

    // Countdown
    this.#initCountdown();
    this.#goToSlide(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelAutoplayTriggers?.();
    if (this.#autoplayInterval) clearInterval(this.#autoplayInterval);
    if (this.#countdownInterval) clearInterval(this.#countdownInterval);
  }

  #goToSlide(index) {
    const { slides, imageSlides, dotsContainer } = this.refs;
    if (!slides || slides.length === 0) return;

    this.#currentSlide = ((index % slides.length) + slides.length) % slides.length;

    slides.forEach((slide, i) => {
      slide.classList.toggle('pv-hero-promo__slide--active', i === this.#currentSlide);
      slide.setAttribute('aria-hidden', i !== this.#currentSlide ? 'true' : 'false');
    });

    if (imageSlides) {
      imageSlides.forEach((slide, i) => {
        slide.classList.toggle('pv-hero-promo__slide--active', i === this.#currentSlide);
        slide.setAttribute('aria-hidden', i !== this.#currentSlide ? 'true' : 'false');
      });
    }

    // Update nav dots
    if (dotsContainer) {
      const dots = dotsContainer.querySelectorAll('.pv-hero-promo__nav-dot');
      dots.forEach((dot, i) => {
        const isActive = i === this.#currentSlide;
        dot.classList.toggle('pv-hero-promo__nav-dot--active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    // Update nav arrow states
    const { prevBtn } = this.refs;
    if (prevBtn) {
      prevBtn.setAttribute('aria-disabled', this.#currentSlide === 0 ? 'true' : 'false');
    }
  }

  #nextSlide() {
    this.#goToSlide(this.#currentSlide + 1);
    this.#resetAutoplay();
  }

  #prevSlide() {
    this.#goToSlide(this.#currentSlide - 1);
    this.#resetAutoplay();
  }

  #resetAutoplay() {
    if (!this.#autoplayInterval) return;
    clearInterval(this.#autoplayInterval);
    const speed = parseInt(this.getAttribute('data-autoplay-speed') || '5000', 10);
    this.#autoplayInterval = setInterval(() => this.#nextSlide(), speed);
  }

  #initCountdown() {
    const endDateStr = this.getAttribute('data-countdown-end');
    if (!endDateStr) return;

    const { countdownHours, countdownMinutes, countdownSeconds } = this.refs;
    if (!countdownHours || !countdownMinutes || !countdownSeconds) return;

    const update = () => {
      const now = Date.now();
      const end = new Date(endDateStr).getTime();
      const diff = Math.max(0, end - now);

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      countdownHours.textContent = String(hours).padStart(2, '0');
      countdownMinutes.textContent = String(minutes).padStart(2, '0');
      countdownSeconds.textContent = String(seconds).padStart(2, '0');

      if (diff <= 0 && this.#countdownInterval) {
        clearInterval(this.#countdownInterval);
      }
    };

    update();
    this.#countdownInterval = setInterval(update, 1000);
  }
}

if (!customElements.get('pv-hero-promo')) {
  customElements.define('pv-hero-promo', PvHeroPromo);
}
